import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import getApiBase from '../components/getApiBase';

export default function CancelEmailChange() {
  const { cancelToken } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [oldEmail, setOldEmail] = useState('');

  useEffect(() => {
    const cancelChange = async () => {
      try {
        const response = await fetch(`${getApiBase()}/api/auth/cancel-email-change/${cancelToken}`, {
          method: 'GET',
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setSuccess(true);
          setOldEmail(data.oldEmail);
          // Redirect to home after 5 seconds
          setTimeout(() => {
            navigate('/');
          }, 5000);
        } else {
          setError(data.message || 'Failed to cancel email change');
        }
      } catch (err) {
        console.error('Cancel email change error:', err);
        setError(
          'An error occurred while cancelling the email change. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    };

    if (cancelToken) {
      cancelChange();
    } else {
      setError('No cancellation token provided');
      setLoading(false);
    }
  }, [cancelToken, navigate]);

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        {loading && (
          <Box>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Cancelling email change...
            </Typography>
          </Box>
        )}

        {!loading && success && (
          <Box>
            <CheckCircleIcon
              sx={{ fontSize: 80, color: 'success.main', mb: 2 }}
            />
            <Typography variant="h4" gutterBottom sx={{ color: 'success.main', fontWeight: 600 }}>
              Email Change Cancelled
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              The email change request has been cancelled successfully.
            </Typography>
            <Box
              sx={{
                bgcolor: '#f5f5f5',
                p: 2,
                borderRadius: 1,
                mb: 3,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Your email remains:
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontFamily: 'monospace', fontWeight: 600, mt: 1 }}
              >
                {oldEmail}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Your account is secure. No changes were made.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Redirecting to home page in 5 seconds...
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/')}
              sx={{ mt: 3 }}
            >
              Go to Home Page Now
            </Button>
          </Box>
        )}

        {!loading && error && (
          <Box>
            <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ color: 'error.main', fontWeight: 600 }}>
              Cancellation Failed
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {error}
            </Typography>
            <Box
              sx={{
                bgcolor: '#fff3cd',
                border: '1px solid #ffc107',
                p: 2,
                borderRadius: 1,
                mt: 3,
                mb: 3,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                💡 Troubleshooting Tips:
              </Typography>
              <Typography variant="body2" component="div" sx={{ textAlign: 'left' }}>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>The cancellation link may have expired (valid for 24 hours)</li>
                  <li>The link may have already been used</li>
                  <li>The email change may have already been completed</li>
                  <li>
                    If you need assistance, please{' '}
                    <a href="/contact" style={{ color: '#1976d2' }}>
                      contact support
                    </a>
                  </li>
                </ul>
              </Typography>
            </Box>
            <Button variant="contained" color="primary" onClick={() => navigate('/')}>
              Go to Home Page
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
}

